import { useState, useEffect, Dispatch, SetStateAction, useRef } from 'react';
import { get, set, del, clear } from 'idb-keyval';
import { validateStorageByKey, setMigrationVersion, STORAGE_MIGRATION_VERSION } from '../validation';

const IDB_AVAILABLE_REF: { current: boolean | null } = { current: null };

async function checkIdbAvailable(): Promise<boolean> {
  if (IDB_AVAILABLE_REF.current !== null) return IDB_AVAILABLE_REF.current;
  try {
    await set('__idb_test__', 1);
    await del('__idb_test__');
    IDB_AVAILABLE_REF.current = true;
  } catch {
    IDB_AVAILABLE_REF.current = false;
  }
  return IDB_AVAILABLE_REF.current;
}

function readFromLocalStorage<T>(key: string, initialValue: T): T {
  try {
    const item = window.localStorage.getItem(key);
    if (item === null) return initialValue;
    const parsed = JSON.parse(item);
    return validateStorageByKey(key, parsed, initialValue);
  } catch {
    return initialValue;
  }
}

function writeToLocalStorage(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`[storage] Quota exceeded for key "${key}":`, error);
    window.dispatchEvent(new CustomEvent('storage-quota-exceeded', { detail: { key } }));
  }
}

function removeFromLocalStorage(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch { /* ignore quota errors on remove */ }
}

const STORAGE_KEYS = [
  'bg2_rooms', 'bg2_project_meta', 'bg2_active_project_id',
  'bg2_safetyMargin', 'bg2_currency', 'bg2_activeRoomId',
  'bg2_services', 'bg2_categories', 'bg2_units', 'bg2_recipes',
  'bg2_library',
];

export async function migrateFromLocalStorage(): Promise<void> {
  for (const key of STORAGE_KEYS) {
    try {
      const inIdb = await get(key);
      if (inIdb !== undefined) continue;
      const item = window.localStorage.getItem(key);
      if (item === null) continue;
      await set(key, JSON.parse(item));
      removeFromLocalStorage(key);
    } catch (e) {
      console.warn(`[storage] Migration failed for key "${key}":`, e);
    }
  }
  setMigrationVersion(STORAGE_MIGRATION_VERSION);
}

export function useStorage<T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => readFromLocalStorage(key, initialValue));
  const [idbReady, setIdbReady] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    checkIdbAvailable().then(available => {
      if (available) {
        get<T>(key).then(stored => {
          if (stored !== undefined) {
            setValue(validateStorageByKey(key, stored, initialValue));
          }
          setIdbReady(true);
        });
      } else {
        setIdbReady(true);
      }
    });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!idbReady) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (IDB_AVAILABLE_REF.current) {
        set(key, value).catch(() => {
          writeToLocalStorage(key, value);
        });
      } else {
        writeToLocalStorage(key, value);
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [key, value, idbReady]);

  return [value, setValue];
}

export function clearAllStorage(): Promise<void> {
  if (IDB_AVAILABLE_REF.current) {
    return clear();
  }
  STORAGE_KEYS.forEach(removeFromLocalStorage);
  return Promise.resolve();
}